import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { describeWorkload } from '@/lib/teamInsight';
import { employeeCodeLabel } from '@/lib/employeeCode';
import type { TeamData } from '@/components/dashboard/TeamOverview';
import { cn } from '@/lib/utils';

/**
 * The people furthest behind — overdue, or under half done.
 *
 * Its own component because two pages compose it differently: the Admin
 * dashboard puts it beside the department rings, My Departments stacks it
 * under them in a side column beside the roster. Same panel either way, so the
 * two can never disagree about who needs chasing.
 */
export function NeedsAttentionPanel({
  members,
  /**
   * How many people to draw, when the whole list is not the point.
   *
   * The dashboard shows a handful — enough to know whether there is a problem
   * and who is at the sharp end of it — and sends the reader to My
   * Departments for the rest. Eight rows of 0% on a dashboard is a list
   * nobody finishes, stacked above everything else on the page.
   *
   * The COUNT in the header stays the true total either way: capping what is
   * drawn must never cap what is reported, or the panel quietly understates
   * the problem it exists to raise.
   */
  limit,
  action,
}: {
  members: TeamData['needsAttention'];
  limit?: number;
  /**
   * A link in the HEADER, top right — not under the list.
   *
   * It sat below the rows until it was moved by request, and the move is an
   * improvement rather than a preference: under a capped list it read as "and
   * then?", which is a footer's job on a list that ran out of room. In the
   * header it reads as "the whole thing is over here", which is what it
   * actually offers — and it stops the panel ending on a full-width bar that
   * was the loudest thing in a small card.
   *
   * Named `action` rather than `footer` so a caller cannot reasonably expect
   * it at the bottom.
   */
  action?: ReactNode;
}) {
  const shown = limit ? members.slice(0, limit) : members;

  return (
    /*
      ── ONE BOX ───────────────────────────────────────────────────────────

      The heading used to sit OUTSIDE the card and the link BELOW it, so a
      panel that is one thing was drawn as three: a loose title, a box, and a
      loose link. Every other panel on these screens — Announcements,
      Upcoming, Platform activity — puts its title on a bordered header inside
      the card, and this one now matches.

      ── Sized to its rows ─────────────────────────────────────────────────

      There was a `fill` prop that stretched the panel to its column and
      scrolled the list inside it, for the dashboard — where the two columns
      were meant to finish level. It went when the dashboard capped the list at
      four: a panel stretched around four rows is two thirds white space, and
      no other call site ever set it.
    */
    <Card className="flex flex-col overflow-hidden">
      <div className="flex shrink-0 items-center gap-2 border-b px-4 py-3">
        <h3 className="text-sm font-semibold">Needs attention</h3>
        {members.length > 0 && (
          <span className="font-mono text-xs tabular-nums text-muted-foreground">
            {members.length}
          </span>
        )}
        {/* `ml-auto`, so the header lays itself out whether or not a caller
            passes one — no empty spacer element. */}
        {action && <div className="ml-auto">{action}</div>}
      </div>

      {members.length === 0 ? (
        <div className="flex flex-col items-center px-6 py-10 text-center">
          <CheckCircle2 className="mb-2 h-7 w-7 text-success" aria-hidden />
          <p className="text-sm font-medium">Everyone is on track</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Nobody is overdue or below 50% completion.
          </p>
        </div>
      ) : (
        <div className="divide-y">
          {shown.map((member) => (
            /*
              The whole row opens that person.

              It named four people and offered one link, to a roster of sixty
              where the reader had to find the name again — undoing the work
              the panel had just done. `?member=` opens their panel on arrival;
              `TeamRoster` reads it.

              A Link rather than a button because it IS a destination: it wants
              middle-click, Ctrl-click and a status bar showing where it goes.
            */
            <Link
              key={member.id}
              to={`/my-departments?member=${member.id}`}
              className="flex items-center gap-3 p-3.5 transition-colors hover:bg-muted/40"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{member.name}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {employeeCodeLabel(member.employeeCode)}
                  {member.department ? ` · ${member.department}` : ''}
                </p>
                {/*
                  Why this person is on the list.

                  A column of identical 0%s says everybody is equally stuck.
                  "4 not started" and "3 in progress, 1 overdue" are the same
                  percentage and completely different conversations.
                */}
                <p className="mt-1 truncate text-xs text-muted-foreground">
                  {describeWorkload(member)}
                </p>
              </div>

              <div className="flex shrink-0 flex-col items-end gap-1">
                <span className="text-sm tabular-nums">
                  {Math.round(member.completionPercent)}%
                </span>
                {member.overdue > 0 && <Badge variant="destructive">{member.overdue} overdue</Badge>}
              </div>

              {/*
                A fixed-width track, so eight rows can be compared down the
                column at a glance. Dropped on the narrowest layouts, where the
                percentage beside it says the same thing in less room.
              */}
              <div className="hidden h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-muted sm:block">
                <div
                  className={cn(
                    'h-full rounded-full',
                    member.completionPercent >= 70
                      ? 'bg-success'
                      : member.completionPercent >= 40
                        ? 'bg-warning'
                        : 'bg-destructive',
                  )}
                  style={{ width: `${Math.max(member.completionPercent, 2)}%` }}
                />
              </div>
            </Link>
          ))}
        </div>
      )}

    </Card>
  );
}
